/**
 * A board with open_to_all_staff lets every active employee in.
 * Membership (owner, team, hq_board_members) still decides a closed board.
 *
 * The flag is absent until sql/2027-02-18_board_open_to_all_staff.sql is
 * applied. Treat that the same as false so the screens keep working.
 */

import { displayName, type Person } from "./types";

export interface BoardAccessShape {
  owner_employee_id: string | null;
  team_id: string | null;
  open_to_all_staff?: boolean | null;
}

/** Directory row everyone signed in can read. Richer employee fields win when RLS returns them. */
export interface DirectoryPerson {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  role: string | null;
}

export function isOpenToAllStaff(board: { open_to_all_staff?: boolean | null } | null | undefined): boolean {
  return board?.open_to_all_staff === true;
}

/**
 * Who the assignee search and the people row may offer.
 * A closed board offers its members. An open board offers every active
 * person we could load, and still keeps a member the directory missed.
 */
export function assigneeChoices(openToAll: boolean, members: Person[], everyone: Person[]): Person[] {
  if (!openToAll) return members;
  const byId = new Map<string, Person>();
  for (const p of everyone) byId.set(p.id, p);
  for (const p of members) if (!byId.has(p.id)) byId.set(p.id, p);
  return [...byId.values()].sort((a, b) => displayName(a).localeCompare(displayName(b)) || a.id.localeCompare(b.id));
}

/**
 * People the card can be assigned to without first adding a member row.
 * On an open board that is everyone we can name, plus the explicit members.
 */
export function openerIds(openToAll: boolean, memberIds: string[], everyone: { id: string }[]): Set<string> {
  if (!openToAll) return new Set(memberIds);
  return new Set([...memberIds, ...everyone.map(p => p.id)]);
}

/**
 * Names for the picker. hq_employees RLS only returns the caller (and, for
 * an admin, everyone). hq_directory is the roster the rest of the staff can
 * read. An employee row overwrites the directory row so preferred name and
 * AI kind survive when we actually received them.
 */
export function mergeStaff(directory: DirectoryPerson[], employees: Person[]): Person[] {
  const byId = new Map<string, Person>();
  for (const d of directory) {
    if (!d.id) continue;
    byId.set(d.id, {
      id: d.id,
      first_name: d.first_name,
      last_name: d.last_name,
      preferred_name: null,
      email: d.email,
      role: d.role,
      employee_kind: null,
    });
  }
  for (const e of employees) byId.set(e.id, e);
  return [...byId.values()].sort((a, b) => displayName(a).localeCompare(displayName(b)) || a.id.localeCompare(b.id));
}

/** Open boards get their own group, so the Team board is not filed under someone's personal boards. */
export function splitBoards<T extends BoardAccessShape>(boards: T[]): { open: T[]; personal: T[]; team: T[] } {
  const open = boards.filter(b => b.open_to_all_staff === true);
  const rest = boards.filter(b => b.open_to_all_staff !== true);
  return {
    open,
    personal: rest.filter(b => b.owner_employee_id),
    team: rest.filter(b => b.team_id),
  };
}

export function missingOpenColumn(error: { message?: string } | null): boolean {
  return !!error && /open_to_all_staff/i.test(error.message ?? "");
}
